import { c as _c } from "react/compiler-runtime";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import React from "react";
import { createPortal } from "react-dom";
import { WaveWinnersDropHeader } from "./header/WaveWinnersDropHeader";
import WaveWinnersDropHeaderAuthorPfp from "./header/WaveWinnersDropHeaderAuthorPfp";
import WaveWinnersDropHeaderTotalVotes from "./header/WaveWinnersDropHeaderTotalVotes";
import WaveWinnersDropHeaderVoters from "./header/WaveWinnersDropHeaderVoters";
import WaveWinnersDropOutcome from "./header/WaveWinnersDropOutcome";
import { WaveWinnersDropContent } from "./WaveWinnersDropContent";
import { WaveWinnerIdentity } from "../identity/WaveWinnerIdentity";
import type { DropContentPresentation } from "@/components/waves/drops/dropContentPresentation";
interface DefaultWaveWinnersDropProps {
  readonly winner: ApiWaveDecisionWinner;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly isApprovalWave?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
}
const getRankHoverClass = (place: number | null): string => {
  return getRankHoverBorderClass(place);
};
const isClickFromCardDom = (event: React.MouseEvent<HTMLDivElement>): boolean => {
  return event.currentTarget.contains(event.target as Node);
};
export const DefaultWaveWinnersDrop: React.FC<DefaultWaveWinnersDropProps> = t0 => {
  const $ = _c(66);
  const {
    winner,
    onDropClick,
    isApprovalWave: t1,
    contentPresentation: t2
  } = t0;
  const isApprovalWave = t1 === undefined ? false : t1;
  const contentPresentation = t2 === undefined ? "default" : t2;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const suppressNextClickRef = React.useRef(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      suppressNextClickRef.current = true;
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleInteractionStart = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (!isClickFromCardDom(event)) {
        return;
      }
      if (!suppressNextClickRef.current) {
        return;
      }
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleClickCapture = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      suppressNextClickRef.current = false;
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handleMenuClickCapture = t5;
  let t6;
  if ($[3] !== hasTouchScreen) {
    t6 = {
      hasTouchScreen,
      onInteractionStart: handleInteractionStart,
      preventDefault: false
    };
    $[3] = hasTouchScreen;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t6);
  let t7;
  if ($[5] !== setIsActive) {
    t7 = nextIsActive => {
      if (!nextIsActive) {
        suppressNextClickRef.current = false;
      }
      setIsActive(nextIsActive);
    };
    $[5] = setIsActive;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const handleMobileMenuOpenChange = t7;
  let t8;
  if ($[7] !== handleMobileMenuOpenChange) {
    t8 = () => {
      handleMobileMenuOpenChange(false);
    };
    $[7] = handleMobileMenuOpenChange;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleMobileMenuClose = t8;
  let t9;
  if ($[9] !== winner.drop) {
    t9 = convertApiDropToExtendedDrop(winner.drop);
    $[9] = winner.drop;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const extendedDrop = t9;
  let t10;
  if ($[11] !== extendedDrop || $[12] !== onDropClick) {
    t10 = event_0 => {
      if (!isClickFromCardDom(event_0)) {
        return;
      }
      onDropClick(extendedDrop);
    };
    $[11] = extendedDrop;
    $[12] = onDropClick;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleClick = t10;
  const userContextRating = winner.drop.context_profile_context?.rating ?? 0;
  const hasUserVoted = userContextRating !== 0;
  const userVote = userContextRating;
  const isUserVoteNegative = userVote < 0;
  const creditType = WAVE_VOTING_LABELS[winner.drop.wave.voting_credit_type] || "votes";
  let t11;
  if ($[14] !== isApprovalWave || $[15] !== winner.place) {
    t11 = isApprovalWave ? "desktop-hover:hover:tw-border-iron-700" : getRankHoverClass(winner.place);
    $[14] = isApprovalWave;
    $[15] = winner.place;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `tw-group tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 ${t11}`;
  let t13;
  if ($[17] !== winner) {
    t13 = <WaveWinnersDropHeaderAuthorPfp winner={winner} />;
    $[17] = winner;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== isApprovalWave || $[20] !== winner) {
    t14 = <WaveWinnersDropHeader winner={winner} showVotingInfo={false} isApprovalWave={isApprovalWave} />;
    $[19] = isApprovalWave;
    $[20] = winner;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== contentPresentation || $[23] !== winner) {
    t15 = <WaveWinnersDropContent winner={winner} isCompetitionDrop={true} contentPresentation={contentPresentation} />;
    $[22] = contentPresentation;
    $[23] = winner;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t14}{t15}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t13 || $[29] !== t16) {
    t17 = <div className="tw-flex tw-flex-1 tw-gap-x-3">{t13}{t16}</div>;
    $[28] = t13;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== extendedDrop || $[32] !== hasTouchScreen) {
    t18 = !hasTouchScreen && <div className="tw-flex tw-flex-shrink-0 tw-items-start"><div className="tw-h-8"><WaveDropActionsOpen drop={extendedDrop} /></div></div>;
    $[31] = extendedDrop;
    $[32] = hasTouchScreen;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t17 || $[35] !== t18) {
    t19 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-justify-between tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t17}{t18}</div>;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== winner.drop) {
    t20 = <WaveWinnerIdentity drop={winner.drop} variant="full" cardVariant="chat" />;
    $[37] = winner.drop;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== winner) {
    t21 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-pt-2"><div className="tw-flex tw-items-center tw-gap-x-4 tw-whitespace-nowrap"><WaveWinnersDropHeaderTotalVotes winner={winner} /><WaveWinnersDropHeaderVoters winner={winner} /></div><div><WaveWinnersDropOutcome winner={winner} /></div></div>;
    $[39] = winner;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== creditType || $[42] !== hasUserVoted || $[43] !== isUserVoteNegative || $[44] !== userVote) {
    t22 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-x-1.5"><div className="tw-flex tw-items-baseline tw-gap-x-1"><span className="tw-text-sm tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:</span><span className={`tw-text-sm tw-font-semibold ${isUserVoteNegative ? "tw-text-rose-500" : "tw-text-emerald-500"}`}>{isUserVoteNegative && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}<span className="tw-font-normal tw-text-iron-400">{creditType}</span></span></div></div>;
    $[41] = creditType;
    $[42] = hasUserVoted;
    $[43] = isUserVoteNegative;
    $[44] = userVote;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t20 || $[47] !== t21 || $[48] !== t22) {
    t23 = <div className="tw-ml-[3.25rem]"><div className="tw-flex tw-flex-col tw-gap-y-2">{t20}{t21}{t22}</div></div>;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t19 || $[51] !== t23 || $[52] !== touchHandlers) {
    t24 = <div className="tw-rounded-xl tw-p-4" {...touchHandlers}>{t19}{t23}</div>;
    $[50] = t19;
    $[51] = t23;
    $[52] = touchHandlers;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== extendedDrop || $[55] !== handleMobileMenuClose || $[56] !== handleMobileMenuOpenChange || $[57] !== hasTouchScreen || $[58] !== isActive || $[59] !== setIsActive) {
    t25 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={handleMobileMenuOpenChange}><div onClickCapture={handleMenuClickCapture} className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={extendedDrop} onOpenChange={handleMobileMenuClose} /><WaveDropMobileMenuCopyLink drop={extendedDrop} onCopy={() => setIsActive(false)} /></div></CommonDropdownItemsMobileWrapper>, document.body);
    $[54] = extendedDrop;
    $[55] = handleMobileMenuClose;
    $[56] = handleMobileMenuOpenChange;
    $[57] = hasTouchScreen;
    $[58] = isActive;
    $[59] = setIsActive;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== handleClick || $[62] !== t12 || $[63] !== t24 || $[64] !== t25) {
    t26 = <div onClickCapture={handleClickCapture} onClick={handleClick} className={t12}>{t24}{t25}</div>;
    $[61] = handleClick;
    $[62] = t12;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
};